import { put, take, select, fork } from "redux-saga/effects";
import { PURGE, REHYDRATE } from "redux-persist";
import moment from "moment";
import { LOAD_USER_SITES_COMPLETE } from "../userSites/actionTypes";
import { defaultLocale, i18n } from "../../../i18n";
import { APP_SETUP_COMPLETE } from "../appSetup/actionTypes";

const INITIAL_STATE = {
  activeProject: {
    jira_key: "",
    sites: [],
    skysparkURL: "",
  },
  activeSite: { label: "", id: "", syncoId: undefined },
  selectedSites: [],
  daysRange: 3,
  locale: defaultLocale,
  isMultiSite: true,
};

// Global site select
export const SELECT_BETA_SITE = "bonfire/betaSelections/SELECT_BETA_SITE";
export const SELECT_BETA_PROJECT = "bonfire/betaSelections/SELECT_BETA_PROJECT";
export const UPDATE_ACTIVE_SITE = "bonfire/betaSelections/UPDATE_ACTIVE_SITE";
export const UPDATE_ACTIVE_PROJECT =
  "bonfire/betaSelections/UPDATE_ACTIVE_PROJECT";
export const UPDATE_SELECTED_SITES =
  "bonfire/betaSelections/UPDATE_SELECTED_SITES";
export const UPDATE_IS_MULTI_SITE_ROUTE =
  "bonfire/betaSelections/UPDATE_IS_MULTI_SITE_ROUTE";

// Triage time range select
export const UPDATE_DAYS_RANGE = "bonfire/betaSelections/UPDATE_DAYS_RANGE";

// Locale option select
export const SET_LOCALE = "bonfire/betaSelections/SET_LOCALE";

// TODO: With multiselect we'll probably end up removing this concept of an active site.
export function reducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case PURGE:
      return {
        ...state,
        activeProject: INITIAL_STATE.activeProject,
      };
    case UPDATE_ACTIVE_PROJECT:
      return {
        ...state,
        activeProject: action.project,
      };
    case UPDATE_ACTIVE_SITE:
      return {
        ...state,
        activeProject: action.project,
        activeSite: action.site,
      };
    case UPDATE_IS_MULTI_SITE_ROUTE:
      return {
        ...state,
        isMultiSite: action.isMultiSite,
      };
    case UPDATE_SELECTED_SITES:
      return {
        ...state,
        selectedSites: action.sites,
      };
    case UPDATE_DAYS_RANGE:
      return {
        ...state,
        daysRange: action.daysRange,
      };
    case SET_LOCALE:
      return {
        ...state,
        locale: action.locale,
      };
    default:
      return state;
  }
}

export function selectActiveProject(projectId) {
  return {
    type: SELECT_BETA_PROJECT,
    projectId,
  };
}

function updateActiveProject(project) {
  return {
    type: UPDATE_ACTIVE_PROJECT,
    project,
  };
}

export function selectActiveSite(syncoId) {
  return {
    type: SELECT_BETA_SITE,
    syncoId,
  };
}

function updateActiveSite(site, project) {
  return {
    type: UPDATE_ACTIVE_SITE,
    site,
    project,
  };
}

export function updateIsMultiSiteRoute(isMultiSite) {
  return {
    type: UPDATE_IS_MULTI_SITE_ROUTE,
    isMultiSite,
  };
}

export function updateSelectedSites(sites) {
  return {
    type: UPDATE_SELECTED_SITES,
    sites,
  };
}

export function updateDaysRange(daysRange = 3) {
  return {
    type: UPDATE_DAYS_RANGE,
    daysRange,
  };
}

export function setLocale(locale) {
  return {
    type: SET_LOCALE,
    locale,
  };
}

// TODO: finish this
export function* selectActiveSiteFlow() {
  while (true) {
    const { syncoId } = yield take(SELECT_BETA_SITE);
    const { activeSite } = yield select((state) => state.betaSelections);
    const { isLoading } = yield select((state) => state.userSites);

    if (isLoading) yield take(LOAD_USER_SITES_COMPLETE);

    if (!activeSite || !activeSite.syncoId || activeSite.syncoId !== syncoId) {
      const { projects, sites } = yield select((state) => state.userSites);
      const site = sites[syncoId];
      const project = projects[site.projectId];

      yield put(updateActiveSite(site, project));
    }
  }
}

export function* selectActiveProjectFlow() {
  while (true) {
    const { projectId } = yield take(SELECT_BETA_PROJECT);
    const { activeProject } = yield select((state) => state.betaSelections);

    if (activeProject.project_pk !== projectId) {
      const { projects } = yield select((state) => state.userSites);
      const project = projects[projectId];

      yield put(updateActiveProject(project));
    }
  }
}

export function* updateSelectedSitesFlow() {
  yield take(APP_SETUP_COMPLETE);

  while (true) {
    const { sites: syncoIds } = yield take(UPDATE_SELECTED_SITES);
    const { activeSite, selectedSites } = yield select(
      (state) => state.betaSelections
    );
    const { sites } = yield select((state) => state.userSites);
    // If the currently active site doesn't exist in the new list of selected
    // sites, update the active site to be the first of the newly selected sites
    // This will ensure that Actions and Insights are never displaying data for a site that isn't selected
    if (!activeSite.syncoId || !syncoIds.includes(activeSite.syncoId)) {
      // If there are already selected sites, use the first site in the list
      // Otherwise use the first from sites available to user
      const firstSite =
        selectedSites.length > 0
          ? selectedSites[0]
          : sites[syncoIds[0]].syncoId;
      yield put(selectActiveSite(firstSite));
    }
  }
}

function* updateLocale() {
  yield take(REHYDRATE);
  const userLocale = yield select((state) => state.betaSelections.locale);

  i18n.activate(userLocale);
  moment.updateLocale(userLocale, {
    relativeTime: {
      future: "%s",
      past: "%s",
    },
  });

  while (true) {
    const { locale } = yield take(SET_LOCALE);
    i18n.activate(locale);
    moment.updateLocale(userLocale, {
      relativeTime: {
        future: "%s",
        past: "%s",
      },
    });
  }
}

export function* selectionsFlow() {
  yield fork(updateLocale);
  yield take(LOAD_USER_SITES_COMPLETE);
  yield fork(selectActiveSiteFlow);
  yield fork(selectActiveProjectFlow);
  yield fork(updateSelectedSitesFlow);
}
